"use client";

import { ReactNode, useRef } from "react";
import { motion, Variants, useInView } from "framer-motion";
import { cn } from "~/lib/utils";
import { useClientMediaQuery } from "~/hooks/useClientMediaQuery";

type MarginValue = `${number}${"px" | "%"}`;
type MarginType =
    | MarginValue
    | `${MarginValue} ${MarginValue}`
    | `${MarginValue} ${MarginValue} ${MarginValue}`
    | `${MarginValue} ${MarginValue} ${MarginValue} ${MarginValue}`;

type RevealProps = {
  children: ReactNode;
  variant?: "fade-up" | "fade-down";
  delay?: number;
  duration?: number;
  className?: string;
  disableDelayOnMobile?: boolean;
  inViewMargin?: MarginType;
};

type RevealCustom = {
  delay: number;
  duration: number;
};

const variantsMap: Record<NonNullable<RevealProps["variant"]>, Variants> = {
    "fade-up": {
        hidden: {
            opacity: 0,
            y: 24,
        },
        visible: ({ delay, duration }: RevealCustom) => ({
            opacity: 1,
            y: 0,
            transition: {
                duration,
                ease: [0.16, 1, 0.3, 1],
                delay,
            },
        }),
    },

    "fade-down": {
        hidden: {
            opacity: 0,
            y: -24,
        },
        visible: ({ delay, duration }: RevealCustom) => ({
            opacity: 1,
            y: 0,
            transition: {
                duration,
                ease: [0.16, 1, 0.3, 1],
                delay,
            },
        }),
    },
};

const Reveal = ({
    children,
    variant = "fade-up",
    delay = 0,
    duration = 2.5,
    className,
    disableDelayOnMobile = false,
    inViewMargin = "-80px",
}: RevealProps) => {
    const ref = useRef<HTMLDivElement | null>(null);

    const isInView = useInView(ref, {
        once: true,
        margin: inViewMargin,
    });

    const isMobile = useClientMediaQuery("(max-width: 767px)");
    const mobileDelayDisabled = disableDelayOnMobile && isMobile === true;

    const animateState = mobileDelayDisabled
        ? "visible"
        : isInView
            ? "visible"
            : "hidden";

    return (
        <motion.div
            ref={ref}
            className={cn("will-change-transform", className)}
            variants={variantsMap[variant]}
            initial="hidden"
            animate={animateState}
            custom={{delay, duration}}
        >
            {children}
        </motion.div>
    );
};

export default Reveal;
